import React, { useCallback, useMemo } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import {
	type ReferencedObjectReadViewType,
	ReferencedObjectsReadView,
} from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/referenced-object/index.tsx';
import { ReferencedObjectPicker } from '@atlassian/jira-servicedesk-insight-editable-referenced-object-picker/src/ui/index.tsx';
import {
	useReferencedObjects,
	getObjectReferenceOptions,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/referenced-object/index.tsx';
import { getAttributeValuesPayload } from '@atlassian/jira-servicedesk-insight-object-details-store/src/common/utils/transform-attributes/index.tsx';
import { useObjectDetails } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/store/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { ObjectAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import type { InsightObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute.tsx';
import type { ObjectDetailsState } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import type { ReferencedObjectPickerOption } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/picker-options.tsx';
import { toCmdbObjectTypeId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { EditViewSpacers } from '../../common/ui/edit-view-spacers/styled.tsx';
import { InlineEditableAttribute } from '../../common/ui/editable-attribute/index.tsx';

type Props = {
	isEditable: boolean;
	attributeType: ObjectAttributeType;
	quickCreateObjects?: boolean;
};

const toFieldValue = (values: Array<ReferencedObjectPickerOption>) =>
	values.map(({ value: objectKey }) => objectKey);

const getObjectProperties = (objectDetails: ObjectDetailsState | null) => {
	const { editableAttributeTypes, attributes = [], objectType, hasAvatar } = objectDetails ?? {};

	if (editableAttributeTypes) {
		const attributesMap = attributes.reduce<Record<string, InsightObjectAttribute>>(
			(accumulator, attribute) =>
				Object.assign(accumulator, {
					[String(attribute.objectTypeAttributeId)]: attribute,
				}),
			{},
		);

		const attributesPayload = editableAttributeTypes.map<{
			objectTypeAttributeId: string;
			objectAttributeValues: {
				value: string | null;
			}[];
		}>(({ id }) => ({
			objectTypeAttributeId: id,
			objectAttributeValues: getAttributeValuesPayload(attributesMap?.[id]),
		}));

		const keyAttribute = attributes.find(
			(attribute) => attribute.objectTypeAttribute.name === 'Key',
		);

		if (keyAttribute) {
			attributesPayload.push({
				objectTypeAttributeId: keyAttribute.objectTypeAttributeId,
				objectAttributeValues: getAttributeValuesPayload(keyAttribute),
			});
		}

		return {
			objectTypeId: toCmdbObjectTypeId(objectType?.id ?? ''),
			attributes: attributesPayload,
			hasAvatar: hasAvatar ?? false,
			avatarUUID: '',
		};
	}

	return undefined;
};

const ObjectAttribute = ({ attributeType, isEditable, quickCreateObjects = false }: Props) => {
	const [{ attribute }] = useEditableAttribute(attributeType);
	const [referencedObjects, setReferencedObjects] = useReferencedObjects(attribute);
	const [objectDetails] = useObjectDetails();
	const objectProperties = useMemo(() => getObjectProperties(objectDetails), [objectDetails]);
	const isMultiObjectPicker = attributeType.maximumCardinality !== 1;
	const storedValue = toFieldValue(getObjectReferenceOptions(attribute));

	const onValueChange = useCallback(
		(values: ReferencedObjectPickerOption[]) => {
			setReferencedObjects(values);
		},

		[setReferencedObjects],
	);
	const resetValue = useCallback(() => {
		setReferencedObjects(getObjectReferenceOptions(attribute));
	}, [attribute, setReferencedObjects]);

	const removedOptions = getObjectReferenceOptions(attribute).filter(
		(val) => !toFieldValue(referencedObjects).includes(val.objectKey),
	);
	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.referenced-object-attribute"
			// Add a key to force a remount when attribute changes because when creating a new object
			// the objectKey value changes after update (CREATE: to XXX-XXXX) and because defaultValue
			// only specifies the value when the input is initially created
			key={toFieldValue(getObjectReferenceOptions(attribute)).join('_')}
			value={toFieldValue(referencedObjects)}
			onChange={onValueChange}
			storedValue={storedValue}
			isEditable={isEditable}
			attributeValuesInfo={{ type: 'object', attributeValues: referencedObjects }}
			scalingEnabled
			attributeType={attributeType}
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<ReferencedObjectsReadView
						referencedObjects={referencedObjects.map<ReferencedObjectReadViewType>((object) => ({
							id: object.id,
							objectKey: object.objectKey,
							avatar: object.avatar,
							label: object.label,
							forceMaxWidth: false,
						}))}
					/>
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, onChange, shouldInputRenderValue, isClearable }) =>
				objectProperties ? (
					<EditViewSpacers.ReferencedObjectPicker>
						<ReferencedObjectPicker
							isClearable={isClearable}
							shouldInputRenderValue={shouldInputRenderValue}
							autoFocus
							menuIsOpen
							objectProperties={objectProperties}
							value={referencedObjects}
							isMultiSelect={isMultiObjectPicker}
							validationState={hasValidationError ? 'error' : 'default'}
							attributeType={attributeType}
							onChange={onChange}
							removedOptions={!shouldInputRenderValue ? removedOptions : []}
							quickCreateObjects={quickCreateObjects}
						/>
					</EditViewSpacers.ReferencedObjectPicker>
				) : (
					<></>
				)
			}
			onResetValue={resetValue}
		/>
	);
};

export const ObjectEditableAttribute = (props: Props) => (
	<JSErrorBoundary
		id="InsightEditableObjectAttribute"
		packageName="jiraServicedeskInsightEditableObjectAttribute"
		teamName="falcons"
		fallback="unmount"
	>
		<ObjectAttribute {...props} />
	</JSErrorBoundary>
);
